import { useEffect, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";
import useLanguagesHooks from "../../../Languages/setLanguage-hooks";
import { ICategory, IMenuItemResponce } from "../../../../Interfaces/responseTypes";
import { ISearchMenuProps } from "./searchMenu";

export interface MenuSearchResult {
    category_id: number;
    category_name: string;
    category_image: string;
    item_id?: number;
    item_name?: string;
    item_image?: string;
}

export interface SearchInputValues {
    search: string;
}

const useSearchHooks = (props: ISearchMenuProps) => {
    const { menu_list, searchHandler, onClose } = props;
    const [searchValue, setSearchValue] = useState<string>("");
    const { register, setValue } = useForm<SearchInputValues | FieldValues>({ mode: "all" });
    const { activeLanguage } = useLanguagesHooks();
    const [results, setResults] = useState<MenuSearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [debouncedValue] = useDebounce(searchValue, 300);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    

    const fetchSearchResults = async (query: string) => {
        setIsLoading(true);
        const normalizedQuery = query.trim().toLowerCase();
        const filteredResults: MenuSearchResult[] = [];

        menu_list.forEach((menu) => {
            const { name_en, name_ru, name_hy } = menu.category;
            if (
                name_en.toLowerCase().includes(normalizedQuery) ||
                name_ru.toLowerCase().includes(normalizedQuery) ||
                name_hy.toLowerCase().includes(normalizedQuery)
            ) {
                filteredResults.push({
                    category_id: menu.category.id,
                    category_name: String(menu.category[`name_${activeLanguage}` as keyof ICategory]),
                    category_image: menu.category.upload_image ?? menu.category.default_image,
                });
            }

            menu.items.forEach((item) => {
                const { name_en, name_ru, name_hy } = item;
                if (
                    name_en.toLowerCase().includes(normalizedQuery) ||
                    name_ru.toLowerCase().includes(normalizedQuery) ||
                    name_hy.toLowerCase().includes(normalizedQuery)
                ) {
                    filteredResults.push({
                        category_id: menu.category.id,
                        category_name: String(menu.category[`name_${activeLanguage}` as keyof ICategory]),
                        category_image: menu.category.upload_image ?? menu.category.default_image,
                        item_id: item.id,
                        item_name: String(item[`name_${activeLanguage}` as keyof IMenuItemResponce]),
                        item_image: item.image,
                    });
                }
            });
        });

        setTimeout(() => {
            setResults(filteredResults);
            setIsLoading(false);
        }, 500);
    };

    useEffect(() => {
        if (debouncedValue) {
            fetchSearchResults(debouncedValue);
        } else {
            setResults([]);
        }
    }, [debouncedValue]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchContainerRef.current &&
                !searchContainerRef.current.contains(event.target as Node)
            ) {
                setSearchValue("");
                setValue("search", "");
                setResults([]);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const onClickResult = (result: MenuSearchResult) => {
        searchHandler(result);
        setSearchValue("");
        setValue("search", "");
        setResults([]);
        onClose && onClose();
    };

    return {
        results,
        isLoading,
        searchValue,
        searchContainerRef,
        register,
        onClickResult,
        setSearchValue,
        handleInputChange,
    };
};

export default useSearchHooks;
